import { TrendingUp, TrendingDown, Calculator, Clock, History } from 'lucide-react'
import type { UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry, registerPresetNode } from '../universal-node-schema'
import { timeSlicePresets } from '../time-slice-presets'
import { barOffsetSummary, conditionCounterSummary, timeSliceSummary } from '../node-labels'

// Bars Condition Counter Node
const barsConditionCounterSchema: UniversalNodeSchema = {
  type: 'barsConditionCounter',
  face: { shortName: 'COUNTER', summary: conditionCounterSummary },
  displayName: 'Condition Counter',
  category: 'utilities',
  icon: Calculator,
  inputHandler: {
    type: 'dual',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'A',
      acceptedTypes: ['number', 'ohlcv'],
      // 'valueHold' (a logic-category node) is allowed as a counter operand: a
      // frozen numeric value is valid to count against (e.g. close > held_value).
      // Scoped to the node type, not the whole 'logic' category, so boolean
      // logic outputs stay blocked. Backtest codegen None-guards the held slot.
      acceptedSources: ['math', 'indicators', 'priceData', 'utilities', 'valueHold'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_2',
      displayName: 'B',
      acceptedTypes: ['number', 'ohlcv'],
      // See input_1: 'valueHold' allowed as a counter operand (held numeric value).
      acceptedSources: ['math', 'indicators', 'priceData', 'utilities', 'valueHold'],
      discriminatedUnion: {
        staticType: 'number',
        defaultStaticValue: 0,
        parameterName: 'inputBType',
      },
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Count',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'operator',
      displayName: 'Operator',
      type: 'select',
      default: '>=',
      options: [
        { value: '>=', label: '>=' },
        { value: '<=', label: '<=' },
        { value: '>', label: '>' },
        { value: '<', label: '<' },
        { value: '==', label: '=' },
        { value: '!=', label: '!=' },
      ],
    },
    {
      name: 'inputBType',
      displayName: 'Input B Type',
      type: 'select',
      default: 'node',
      options: [
        { value: 'node', label: 'Node' },
        { value: 'static', label: 'Static' },
      ],
    },
    {
      name: 'countMode',
      displayName: 'Count mode',
      type: 'select',
      default: 'consecutive',
      options: [
        { value: 'consecutive', label: 'Consecutive' },
        { value: 'session', label: 'Session total' },
      ],
      description: 'Count true bars, hold on false, and reset by configured local session date.',
    },
  ],
  hasEquation: true,
}

// Rolling Maximum
const rollingMaximumSchema: UniversalNodeSchema = {
  type: 'rollingMaximum',
  face: { shortName: 'ROLLING-MAX' },
  displayName: 'Rolling Maximum',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        // Default to the natural max line (high) — matches the engine's
        // RollingMax default (source=None -> bar.high). 'close' made a
        // price-sourced Rolling Maximum compute max-of-close ("Min Max only
        // Close"). A wired indicator band overrides via the dynamic selection.
        defaultField: 'high',
      },
    },
  ],
  outputs: [
    {
      id: 'maximum',
      displayName: 'Maximum',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'reference',
      displayName: 'Reference',
      type: 'select',
      default: 'session',
      options: [
        { value: 'session', label: 'Session' },
        { value: 'xbars', label: 'X Bars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
      dependsOn: {
        parameter: 'reference',
        values: ['xbars'],
      },
    },
  ],
}

// Rolling Minimum
const rollingMinimumSchema: UniversalNodeSchema = {
  type: 'rollingMinimum',
  face: { shortName: 'ROLLING-MIN' },
  displayName: 'Rolling Minimum',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        // Default to the natural min line (low) — matches the engine's
        // RollingMin default (source=None -> bar.low). 'close' made a
        // price-sourced Rolling Minimum compute min-of-close ("Min Max only
        // Close"). A wired indicator band overrides via the dynamic selection.
        defaultField: 'low',
      },
    },
  ],
  outputs: [
    {
      id: 'minimum',
      displayName: 'Minimum',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'reference',
      displayName: 'Reference',
      type: 'select',
      default: 'session',
      options: [
        { value: 'session', label: 'Session' },
        { value: 'xbars', label: 'X Bars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
      dependsOn: {
        parameter: 'reference',
        values: ['xbars'],
      },
    },
  ],
}

// Rolling Average
const rollingAverageSchema: UniversalNodeSchema = {
  type: 'rollingAverage',
  face: { shortName: 'ROLLING-AVG' },
  displayName: 'Rolling Average',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'average',
      displayName: 'Average',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'reference',
      displayName: 'Reference',
      type: 'select',
      default: 'session',
      options: [
        { value: 'session', label: 'Session' },
        { value: 'xbars', label: 'X Bars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
      dependsOn: {
        parameter: 'reference',
        values: ['xbars'],
      },
    },
  ],
}

// Rolling Sum
const rollingSumSchema: UniversalNodeSchema = {
  type: 'rollingSum',
  face: { shortName: 'ROLLING-SUM' },
  displayName: 'Rolling Sum',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'sum',
      displayName: 'Sum',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'reference',
      displayName: 'Reference',
      type: 'select',
      default: 'session',
      options: [
        { value: 'session', label: 'Session' },
        { value: 'xbars', label: 'X Bars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
      dependsOn: {
        parameter: 'reference',
        values: ['xbars'],
      },
    },
  ],
}

// Rolling Range
const rollingRangeSchema: UniversalNodeSchema = {
  type: 'rollingRange',
  face: { shortName: 'ROLLING-RNG' },
  displayName: 'Rolling Range',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'range',
      displayName: 'Range',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'reference',
      displayName: 'Reference',
      type: 'select',
      default: 'session',
      options: [
        { value: 'session', label: 'Session' },
        { value: 'xbars', label: 'X Bars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
      dependsOn: {
        parameter: 'reference',
        values: ['xbars'],
      },
    },
  ],
}

// Rolling Midpoint
const rollingMidpointSchema: UniversalNodeSchema = {
  type: 'rollingMidpoint',
  face: { shortName: 'ROLLING-MID' },
  displayName: 'Rolling Midpoint',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'midpoint',
      displayName: 'Midpoint',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'reference',
      displayName: 'Reference',
      type: 'select',
      default: 'session',
      options: [
        { value: 'session', label: 'Session' },
        { value: 'xbars', label: 'X Bars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 1,
      max: 200,
      dependsOn: {
        parameter: 'reference',
        values: ['xbars'],
      },
    },
  ],
}

// Bar Size Node
const barSizeSchema: UniversalNodeSchema = {
  type: 'barSize',
  face: { shortName: 'BAR-SIZE' },
  displayName: 'Bar Size',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData'],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Bar Size',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'sizeType',
      displayName: 'Size Type',
      type: 'select',
      default: 'body',
      options: [
        { value: 'body', label: 'Body' },
        { value: 'total', label: 'Total' },
      ],
    },
    {
      name: 'outputType',
      displayName: 'Output Type',
      type: 'select',
      default: 'Percent',
      options: [
        { value: 'Percent', label: 'Percent' },
        { value: 'Absolute', label: 'Absolute' },
      ],
    },
  ],
}

// Wick Size Node
const wickSizeSchema: UniversalNodeSchema = {
  type: 'wickSize',
  face: { shortName: 'WICK-SIZE' },
  displayName: 'Wick Size',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData'],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Wick Size',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'wickType',
      displayName: 'Wick Type',
      type: 'select',
      default: 'upper',
      options: [
        { value: 'upper', label: 'Upper' },
        { value: 'lower', label: 'Lower' },
      ],
    },
    {
      name: 'outputType',
      displayName: 'Output Type',
      type: 'select',
      default: 'Percent',
      options: [
        { value: 'Percent', label: 'Percent' },
        { value: 'Absolute', label: 'Absolute' },
      ],
    },
  ],
}

// Time Slice Indicator Node
const timeSliceIndicatorSchema: UniversalNodeSchema = {
  type: 'timeSlice',
  face: { shortName: 'TIME-SLICE', summary: timeSliceSummary },
  displayName: 'Time Slice',
  category: 'utilities',
  icon: Clock,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Value',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'mode',
      displayName: 'Mode',
      type: 'select',
      default: 'last',
      options: [
        { value: 'first', label: 'First' },
        { value: 'last', label: 'Last' },
        { value: 'max', label: 'Max' },
        { value: 'min', label: 'Min' },
      ],
    },
    {
      name: 'startOffset',
      displayName: 'Start Days Back',
      type: 'int',
      default: 0,
      min: 0,
      max: 999,
    },
    {
      name: 'startTime',
      displayName: 'Start Time',
      type: 'time',
      default: '09:30',
      timePresets: [
        { label: '04:00', value: '04:00' },
        { label: '09:30', value: '09:30' },
        { label: '12:00', value: '12:00' },
        { label: '14:00', value: '14:00' },
        { label: '16:00', value: '16:00' },
        { label: '20:00', value: '20:00' },
      ],
    },
    {
      name: 'endOffset',
      displayName: 'End Days Back',
      type: 'int',
      default: 0,
      min: 0,
      max: 999,
    },
    {
      name: 'endTime',
      displayName: 'End Time',
      type: 'time',
      default: '16:00',
      timePresets: [
        { label: '04:00', value: '04:00' },
        { label: '09:30', value: '09:30' },
        { label: '12:00', value: '12:00' },
        { label: '14:00', value: '14:00' },
        { label: '16:00', value: '16:00' },
        { label: '20:00', value: '20:00' },
      ],
    },
  ],
}

// Bar Offset Node
const barOffsetSchema: UniversalNodeSchema = {
  type: 'barOffset',
  face: { shortName: 'OFFSET', summary: barOffsetSummary },
  displayName: 'Bar Offset',
  category: 'utilities',
  icon: History,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'output',
      displayName: 'Output',
      type: 'number', // Default type; actual type mirrors connected input
      passthrough: true,
    },
  ],
  parameters: [
    {
      name: 'offset',
      displayName: 'Bars Back',
      type: 'int',
      default: 1,
      min: 1,
      max: 100,
    },
  ],
}

// Signal Counter — counts the consecutive-true streak of a boolean signal
const signalCounterSchema: UniversalNodeSchema = {
  type: 'signalCounter',
  displayName: 'Signal Counter',
  category: 'utilities',
  icon: Calculator,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['boolean'],
      // Boolean producers (condition, crossover are 'logic'); mirror valueHold's condition input.
      acceptedSources: ['logic', 'filters', 'utilities', 'math', 'indicators'],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Count',
      type: 'number',
    },
  ],
  parameters: [],
}

// Time Point Node
const timePointSchema: UniversalNodeSchema = {
  type: 'timePoint',
  face: { shortName: 'TIME-POINT' },
  displayName: 'Time Point',
  category: 'utilities',
  icon: TrendingUp,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: '',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Value',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'daysBack',
      displayName: 'Days Back',
      type: 'int',
      default: 1,
      min: 1,
      max: 365,
    },
    {
      name: 'time',
      displayName: 'Time',
      type: 'time',
      default: '09:30',
      timePresets: [
        { label: '04:00', value: '04:00' },
        { label: '09:30', value: '09:30' },
        { label: '12:00', value: '12:00' },
        { label: '14:00', value: '14:00' },
        { label: '16:00', value: '16:00' },
        { label: '20:00', value: '20:00' },
      ],
    },
  ],
}

/**
 * Registration function to add all utility node schemas to the universal registry
 */
export function registerUtilitySchemas(): void {
  // Register all utility node schemas (all 15 utility nodes)
  UniversalNodeRegistry.register(barSizeSchema)
  UniversalNodeRegistry.register(barsConditionCounterSchema)
  UniversalNodeRegistry.register(rollingMaximumSchema)
  UniversalNodeRegistry.register(rollingMinimumSchema)
  UniversalNodeRegistry.register(rollingAverageSchema)
  UniversalNodeRegistry.register(rollingSumSchema)
  UniversalNodeRegistry.register(rollingRangeSchema)
  UniversalNodeRegistry.register(rollingMidpointSchema)
  UniversalNodeRegistry.register(timePointSchema)
  UniversalNodeRegistry.register(timeSliceIndicatorSchema)
  UniversalNodeRegistry.register(wickSizeSchema)
  UniversalNodeRegistry.register(barOffsetSchema)
  UniversalNodeRegistry.register(signalCounterSchema)

  // Register Time Slice preset nodes
  Object.entries(timeSlicePresets).forEach(([presetId, preset]) => {
    const icon =
      preset.parameters.mode === 'max'
        ? TrendingUp
        : preset.parameters.mode === 'min'
          ? TrendingDown
          : Clock

    registerPresetNode(
      timeSliceIndicatorSchema,
      presetId,
      preset.displayName,
      preset.description,
      preset.parameters,
      icon,
    )
  })

  // console.log('Registered 12 Utility Node Schemas:', 'Bar Size, Condition Counter, Rolling Maximum, Rolling Minimum, Rolling Average, Rolling Sum, Rolling Range, Rolling Midpoint, Time Point, Time Slice Indicator, Wick Size, Bar Offset');
}

// Export individual schemas
export {
  barSizeSchema,
  barsConditionCounterSchema,
  rollingMaximumSchema,
  rollingMinimumSchema,
  rollingAverageSchema,
  rollingSumSchema,
  rollingRangeSchema,
  rollingMidpointSchema,
  timePointSchema,
  timeSliceIndicatorSchema,
  wickSizeSchema,
  barOffsetSchema,
  signalCounterSchema,
}
